/**
 * The small glyph beside a category, drawn inline.
 *
 * The design uses the Phosphor icon font — `ph-list-checks`, `ph-flow-arrow`, `ph-book-open`,
 * `ph-brain`. Adding an icon font for four glyphs would be a new dependency and a second network
 * font on a page that already loads two, so these are four inline paths that read as the same
 * four things. Stroked in `currentColor`, so the colour comes from the category's own class and
 * never from here.
 *
 * Always `aria-hidden`: the word is beside every one of these, on every screen that uses them.
 * An icon that repeats the adjacent label into a screen reader is noise, not information.
 */

import type { NoteCategory } from "../lib/api/types";

const PATHS: Record<NoteCategory, string> = {
  // list-checks: two ticks and three rules
  rule: "M3 6.5 4.6 8 7.5 5M3 14.5 4.6 16 7.5 13M11 6.5h10M11 12h10M11 17.5h10",
  // flow-arrow: a box, an elbow, and an arrowhead
  flow: "M3 5.5h6v5H3zM15 13.5h6v5h-6zM6 10.5v4a3 3 0 0 0 3 3h6M13 14l2 2-2 2",
  // book-open: a spine with two leaves
  guide: "M12 6.5v13M12 6.5C10.5 5 8.2 4.5 4 4.5v13c4.2 0 6.5.5 8 2M12 6.5c1.5-1.5 3.8-2 8-2v13c-4.2 0-6.5.5-8 2",
  // brain: a rounded lobe with a fold
  knowledge:
    "M12 5.2a3 3 0 0 0-5.4 1.4A3 3 0 0 0 4.7 12a3 3 0 0 0 2.1 4.9 3 3 0 0 0 5.2 1.6zM12 5.2a3 3 0 0 1 5.4 1.4A3 3 0 0 1 19.3 12a3 3 0 0 1-2.1 4.9 3 3 0 0 1-5.2 1.6zM12 9.5h2.5",
};

export function CategoryIcon({ category }: { category: NoteCategory }) {
  return (
    <svg
      className="category-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[category]} />
    </svg>
  );
}
